/**
 * The sign-in page's noticeboard: what the server sends (see
 * backend/apps/core/sign_in.py) turned into what each slide says. Everything
 * here is public, so it is only ever dates, titles and places.
 */

export const SCHOOL_SLOGAN = "Rewriting the story of illiteracy";

export type HighlightSlide =
  | { kind: "term"; term: string; year: string; status: "in_session" | "upcoming"; start_date: string; end_date: string; break_start: string | null; break_end: string | null }
  | { kind: "fees"; term: string; due_date: string }
  | { kind: "event"; title: string; description: string; start_date: string; end_date: string; start_time: string | null; end_time: string | null; venue: string }
  | { kind: "notice"; title: string; body: string; published_at: string }
  | { kind: "admissions"; open_classes: number }
  | { kind: "brand" };

export interface SlideView {
  key: string;
  kind: HighlightSlide["kind"];
  eyebrow: string;
  title: string;
  body: string;
  /** The date block on the left; omitted slides show an icon instead. */
  chip?: { day: string; month: string };
  meta: { icon: "clock" | "place" | "calendar"; text: string }[];
  link?: { to: string; label: string };
}

const at = (iso: string) => new Date(`${iso.slice(0, 10)}T00:00:00`);
const dayMonth = (iso: string) => at(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short" });
const full = (iso: string) => at(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
const chipOf = (iso: string) => ({ day: String(at(iso).getDate()).padStart(2, "0"), month: at(iso).toLocaleDateString("en-GB", { month: "short" }).toUpperCase() });

/** "09:00:00" → "9:00 am". */
export function clock(time: string | null | undefined) {
  if (!time) return "";
  const [hours, minutes] = time.split(":").map(Number);
  const suffix = hours >= 12 ? "pm" : "am";
  return `${hours % 12 || 12}:${String(minutes ?? 0).padStart(2, "0")} ${suffix}`;
}

/** "7 Sept – 4 Dec 2026", or one day when both are the same. */
export function span(start: string, end: string) {
  if (!end || start.slice(0, 10) === end.slice(0, 10)) return full(start);
  return `${dayMonth(start)} – ${full(end)}`;
}

export function slideView(slide: HighlightSlide, index: number): SlideView {
  const key = `${slide.kind}-${index}`;
  switch (slide.kind) {
    case "term": {
      const name = [slide.term, slide.year].filter(Boolean).join(", ");
      const meta: SlideView["meta"] = [{ icon: "calendar", text: span(slide.start_date, slide.end_date) }];
      if (slide.break_start) meta.push({ icon: "calendar", text: `Mid-term break ${span(slide.break_start, slide.break_end ?? slide.break_start)}` });
      return {
        key,
        kind: slide.kind,
        eyebrow: "School calendar",
        title: slide.status === "in_session" ? `${name} is in session` : `${name} begins ${dayMonth(slide.start_date)}`,
        body: slide.status === "in_session" ? `The term closes on ${full(slide.end_date)}.` : "Get ready for the new term: uniforms, requirements and fees.",
        chip: chipOf(slide.status === "in_session" ? slide.end_date : slide.start_date),
        meta,
      };
    }
    case "fees":
      return {
        key,
        kind: slide.kind,
        eyebrow: "Fees",
        title: `${slide.term} fees are due by ${dayMonth(slide.due_date)}`,
        body: "Sign in to see your balance, statements and receipts, and to pay.",
        chip: chipOf(slide.due_date),
        meta: [{ icon: "calendar", text: `Due ${full(slide.due_date)}` }],
      };
    case "event": {
      const time = [clock(slide.start_time), clock(slide.end_time)].filter(Boolean).join(" – ");
      const meta: SlideView["meta"] = [{ icon: "calendar", text: span(slide.start_date, slide.end_date) }];
      if (time) meta.push({ icon: "clock", text: time });
      if (slide.venue) meta.push({ icon: "place", text: slide.venue });
      return { key, kind: slide.kind, eyebrow: "Upcoming event", title: slide.title, body: slide.description, chip: chipOf(slide.start_date), meta };
    }
    case "notice":
      return {
        key,
        kind: slide.kind,
        eyebrow: "School notice",
        title: slide.title,
        body: slide.body,
        meta: [{ icon: "calendar", text: `Posted ${full(slide.published_at)}` }],
      };
    case "admissions":
      return {
        key,
        kind: slide.kind,
        eyebrow: "Admissions",
        title: "Applications are open",
        body: `Places are available in ${slide.open_classes} ${slide.open_classes === 1 ? "class" : "classes"}. Apply online and follow your application as it is reviewed.`,
        meta: [],
        link: { to: "/apply", label: "Apply online" },
      };
    default:
      return {
        key,
        kind: "brand",
        eyebrow: "Our promise",
        title: `${SCHOOL_SLOGAN}.`,
        body: "Every learner reading, writing and thinking with confidence. One portal keeps staff, parents and students close to that work.",
        meta: [],
      };
  }
}

/** The school's slides, then the promise, which also stands alone when nothing is scheduled. */
export const slidesFor = (slides: HighlightSlide[] | undefined) => [...(slides ?? []), { kind: "brand" } as HighlightSlide].map(slideView);
